import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { businessProfileService } from '../services/businessProfileService';
import type { BusinessProfileDetail } from '../types/businessProfile';

// The auth payload's role shape has drifted over time - tolerate both the
// nested `userRoles: [{ role: { name } }]` form and a flat `roles: string[]`.
const userHasBusinessRole = (user: unknown): boolean => {
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  const userRoles = u.userRoles;
  if (Array.isArray(userRoles)) {
    if (userRoles.some((ur) => {
      const r = ur as { name?: string; role?: { name?: string } };
      return (r?.role?.name ?? r?.name) === 'Business';
    })) {
      return true;
    }
  }
  const roles = u.roles;
  if (Array.isArray(roles) && roles.includes('Business')) return true;
  return false;
};

type BusinessProfileContextType = {
  businessProfile: BusinessProfileDetail | null;
  isLoading: boolean;
  error: string | null;
  hasProfile: boolean;
  refetch: () => Promise<void>;
};

const BusinessProfileContext = createContext<BusinessProfileContextType | undefined>(undefined);

export const BusinessProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, isAuthenticated, user, refreshAuth } = useAuth();
  const [businessProfile, setBusinessProfile] = useState<BusinessProfileDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const roleHealAttempted = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setBusinessProfile(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profile = await businessProfileService.getProfile(token);
      setBusinessProfile(profile);
    } catch (err) {
      // 404 means no profile exists - this is expected for new users
      if (err && typeof err === 'object' && 'status' in err && err.status === 404) {
        setBusinessProfile(null);
      } else {
        console.error('Failed to fetch business profile:', err);
        setError('Failed to load business profile');
        setBusinessProfile(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  // Fetch profile when auth state changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Self-heal stale tokens: a user who owns a business profile but whose token
  // carries no "Business" role claim (e.g. profile created after sign-in, before
  // the role was granted server-side) gets 403s on every role-gated endpoint.
  // The backend now grants the role; refresh once to pick it up without a manual
  // logout. Guarded so it runs at most once per session.
  useEffect(() => {
    if (!businessProfile || roleHealAttempted.current) return;
    if (userHasBusinessRole(user)) return;
    roleHealAttempted.current = true;
    refreshAuth().catch((err) => {
      console.error('Role self-heal token refresh failed:', err);
    });
  }, [businessProfile, user, refreshAuth]);

  const value: BusinessProfileContextType = {
    businessProfile,
    isLoading,
    error,
    hasProfile: !!businessProfile,
    refetch: fetchProfile,
  };

  return (
    <BusinessProfileContext.Provider value={value}>
      {children}
    </BusinessProfileContext.Provider>
  );
};

export const useBusinessProfile = () => {
  const context = useContext(BusinessProfileContext);
  if (context === undefined) {
    throw new Error('useBusinessProfile must be used within a BusinessProfileProvider');
  }
  return context;
};
